import { motion, useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';

export default function SagaPatternInfographic() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [step, setStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (isInView && !isPlaying) {
      setIsPlaying(true);
    }
  }, [isInView, isPlaying]);

  useEffect(() => {
    if (isPlaying && step < 4) {
      const timer = setTimeout(() => setStep(s => s + 1), 1500);
      return () => clearTimeout(timer);
    }
  }, [isPlaying, step]);

  return (
    <div ref={ref} className="relative w-full">
      {/* Friendly explanation */}
      <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <span className="text-2xl">🔄</span> What's the Saga Pattern?
        </h3>
        <p className="text-gray-600 leading-relaxed">
          When you bid, <strong>multiple things must happen together</strong>: 
          reserve your money, record the bid, update the auction. 
          But what if step 2 fails? We need to <strong>undo step 1</strong>!
        </p>
        <ul className="mt-3 space-y-2 text-gray-600">
          <li className="flex items-start gap-2">
            <span className="text-green-500 mt-1">✓</span>
            <span>A <strong>coordinator</strong> manages multi-step operations</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-500 mt-1">✓</span>
            <span>If something fails, it <strong>automatically undoes</strong> previous steps</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-500 mt-1">✓</span>
            <span>Your money is <strong>never stuck</strong> in limbo</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-500 mt-1">✓</span>
            <span>Works even if the internet drops mid-transaction</span>
          </li>
        </ul>
      </div>

      {/* Animated Infographic */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-sm font-semibold text-gray-500">The Bidding Saga: Step by Step</h4>
          <button
            onClick={() => { setStep(0); setIsPlaying(true); }}
            className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-lg hover:bg-blue-200 transition-colors"
          >
            🔄 Replay
          </button>
        </div>

        <svg viewBox="0 0 500 350" className="w-full h-auto">
          {/* Background */}
          <rect x="0" y="0" width="500" height="350" fill="#fafafa" rx="12" />

          {/* Step 1: Wallet */}
          <motion.g
            animate={{ 
              opacity: step >= 0 ? 1 : 0.3,
              scale: step === 1 ? 1.05 : 1
            }}
            transition={{ duration: 0.3 }}
          >
            <rect x="30" y="50" width="120" height="80" rx="12" 
              fill={step >= 1 ? '#dcfce7' : '#f3f4f6'} 
              stroke={step >= 1 ? '#22c55e' : '#d1d5db'} 
              strokeWidth="2" 
            />
            <text x="90" y="80" textAnchor="middle" fontSize="20">💰</text>
            <text x="90" y="100" textAnchor="middle" fontSize="10" fill="#374151" fontWeight="500">Your Wallet</text>
            <text x="90" y="115" textAnchor="middle" fontSize="9" fill="#6b7280">Reserve £100</text>
            {step >= 1 && (
              <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }}>
                <circle cx="135" cy="60" r="12" fill="#22c55e" />
                <text x="135" y="65" textAnchor="middle" fontSize="12" fill="white">✓</text>
              </motion.g>
            )}
          </motion.g>

          {/* Arrow 1→2 */}
          <motion.g animate={{ opacity: step >= 1 ? 1 : 0.3 }}>
            <line x1="155" y1="90" x2="185" y2="90" stroke={step >= 1 ? '#22c55e' : '#d1d5db'} strokeWidth="2" />
            <polygon points="190,90 180,85 180,95" fill={step >= 1 ? '#22c55e' : '#d1d5db'} />
          </motion.g>

          {/* Step 2: Auction */}
          <motion.g
            animate={{ 
              opacity: step >= 1 ? 1 : 0.3,
              scale: step === 2 ? 1.05 : 1
            }}
            transition={{ duration: 0.3 }}
          >
            <rect x="195" y="50" width="120" height="80" rx="12" 
              fill={step >= 2 ? '#dcfce7' : '#f3f4f6'} 
              stroke={step >= 2 ? '#22c55e' : '#d1d5db'} 
              strokeWidth="2" 
            />
            <text x="255" y="80" textAnchor="middle" fontSize="20">🏷️</text>
            <text x="255" y="100" textAnchor="middle" fontSize="10" fill="#374151" fontWeight="500">Auction</text>
            <text x="255" y="115" textAnchor="middle" fontSize="9" fill="#6b7280">Record bid</text>
            {step >= 2 && (
              <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }}>
                <circle cx="300" cy="60" r="12" fill="#22c55e" />
                <text x="300" y="65" textAnchor="middle" fontSize="12" fill="white">✓</text>
              </motion.g>
            )}
          </motion.g>

          {/* Arrow 2→3 */}
          <motion.g animate={{ opacity: step >= 2 ? 1 : 0.3 }}>
            <line x1="320" y1="90" x2="350" y2="90" stroke={step >= 2 ? '#22c55e' : '#d1d5db'} strokeWidth="2" />
            <polygon points="355,90 345,85 345,95" fill={step >= 2 ? '#22c55e' : '#d1d5db'} />
          </motion.g>

          {/* Step 3: Confirm */}
          <motion.g
            animate={{ 
              opacity: step >= 2 ? 1 : 0.3,
              scale: step === 3 ? 1.05 : 1
            }}
            transition={{ duration: 0.3 }}
          >
            <rect x="360" y="50" width="120" height="80" rx="12" 
              fill={step >= 3 ? '#dcfce7' : '#f3f4f6'} 
              stroke={step >= 3 ? '#22c55e' : '#d1d5db'} 
              strokeWidth="2" 
            />
            <text x="420" y="80" textAnchor="middle" fontSize="20">✅</text>
            <text x="420" y="100" textAnchor="middle" fontSize="10" fill="#374151" fontWeight="500">Confirm</text>
            <text x="420" y="115" textAnchor="middle" fontSize="9" fill="#6b7280">Lock funds</text>
            {step >= 3 && (
              <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }}>
                <circle cx="465" cy="60" r="12" fill="#22c55e" />
                <text x="465" y="65" textAnchor="middle" fontSize="12" fill="white">✓</text>
              </motion.g>
            )}
          </motion.g>

          {/* Success result */}
          {step >= 4 && (
            <motion.g
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <rect x="170" y="145" width="170" height="40" rx="8" fill="#22c55e" />
              <text x="255" y="170" textAnchor="middle" fontSize="12" fill="white" fontWeight="bold">
                🎉 Bid Placed Successfully!
              </text>
            </motion.g>
          )}

          {/* The Saga Coordinator */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.5 }}
          >
            <rect x="140" y="200" width="230" height="60" rx="12" fill="#ede9fe" stroke="#8b5cf6" strokeWidth="2" />
            <text x="255" y="225" textAnchor="middle" fontSize="16">🎯</text>
            <text x="255" y="245" textAnchor="middle" fontSize="11" fill="#6d28d9" fontWeight="600">Saga Coordinator</text>
            <text x="255" y="258" textAnchor="middle" fontSize="8" fill="#7c3aed">"I make sure everything happens correctly"</text>
          </motion.g>

          {/* Compensation explanation */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 1 }}
          >
            <rect x="30" y="280" width="215" height="55" rx="8" fill="#fef2f2" stroke="#fca5a5" strokeWidth="1" />
            <text x="137" y="300" textAnchor="middle" fontSize="10" fill="#991b1b" fontWeight="600">
              ❌ If Step 2 Fails...
            </text>
            <text x="137" y="315" textAnchor="middle" fontSize="9" fill="#7f1d1d">
              Saga automatically undoes Step 1
            </text>
            <text x="137" y="328" textAnchor="middle" fontSize="9" fill="#22c55e" fontWeight="500">
              💰 Your money is released!
            </text>
          </motion.g>

          {/* Auto-recovery */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 1.2 }}
          >
            <rect x="260" y="280" width="215" height="55" rx="8" fill="#fef9c3" stroke="#fde047" strokeWidth="1" />
            <text x="367" y="300" textAnchor="middle" fontSize="10" fill="#854d0e" fontWeight="600">
              🔌 Internet Drops Mid-Bid?
            </text>
            <text x="367" y="315" textAnchor="middle" fontSize="9" fill="#713f12">
              Saga remembers where it was
            </text>
            <text x="367" y="328" textAnchor="middle" fontSize="9" fill="#22c55e" fontWeight="500">
              ⚡ Resumes automatically!
            </text>
          </motion.g>
        </svg>
      </div>

      {/* Real-world analogy */}
      <div className="bg-green-50 rounded-xl p-5 border border-green-100">
        <div className="flex items-start gap-3">
          <span className="text-2xl">✈️</span>
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Think of Booking a Holiday</h4>
            <p className="text-sm text-gray-600">
              You book a flight, then a hotel. If the hotel booking fails, you'd want the flight 
              booking cancelled too, right? A Saga is like a <strong>smart travel agent</strong> 
              that handles all this automatically—if anything fails, it unbooks everything.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
